import { useNavigate, useParams } from 'react-router';
import type { LucideIcon } from 'lucide-react';
import {
  ArrowRight,
  Award,
  BarChart3,
  Briefcase,
  Calendar,
  CheckCircle,
  Clock,
  Code,
  DollarSign,
  FileText,
  GitBranch,
  MessageSquare,
  Target,
  TrendingUp,
  Users,
} from 'lucide-react';
import { Button } from '../components/ui/button';

type StakeholderType = 'developer' | 'manager' | 'executive' | 'investor' | 'hr' | 'partner' | 'demo';
type StakeholderTone = 'brand' | 'accent' | 'success' | 'insight' | 'warning' | 'support';

interface StakeholderFeature {
  icon: LucideIcon;
  title: string;
  description: string;
  tone: StakeholderTone;
}

interface StakeholderBenefit {
  title: string;
  description: string;
}

interface StakeholderWorkflowStep {
  title: string;
  description: string;
}

interface StakeholderData {
  title: string;
  description: string;
  ctaText: string;
  icon: LucideIcon;
  tone: StakeholderTone;
  features: StakeholderFeature[];
  benefits: StakeholderBenefit[];
  workflow: StakeholderWorkflowStep[];
  testimonial: string;
  testimonialAuthor: string;
}

const PAGE_SECTION_CLASS = 'container mx-auto px-4';
const SURFACE_CARD_CLASS =
  'rounded-2xl border border-border bg-card p-6 shadow-sm transition-shadow';
const HIGHLIGHT_SECTION_CLASS =
  'border-y border-border bg-gradient-to-r from-chart-1/10 via-chart-3/10 to-chart-4/10';

const STAKEHOLDER_TONE_STYLES: Record<StakeholderTone, { panel: string; icon: string }> = {
  brand: {
    panel: 'border border-primary/20 bg-primary/10',
    icon: 'text-primary',
  },
  accent: {
    panel: 'border border-chart-1/30 bg-chart-1/10',
    icon: 'text-chart-1',
  },
  success: {
    panel: 'border border-chart-2/30 bg-chart-2/10',
    icon: 'text-chart-2',
  },
  insight: {
    panel: 'border border-chart-3/30 bg-chart-3/10',
    icon: 'text-chart-3',
  },
  warning: {
    panel: 'border border-chart-4/30 bg-chart-4/10',
    icon: 'text-chart-4',
  },
  support: {
    panel: 'border border-chart-5/30 bg-chart-5/10',
    icon: 'text-chart-5',
  },
};

const STAKEHOLDER_DATA: Record<StakeholderType, StakeholderData> = {
  developer: {
    title: 'Built for Developers',
    description:
      'Focus on what you do best - writing great code. Track your tasks, log time, share updates, and see your impact.',
    ctaText: 'Start Coding',
    icon: Code,
    tone: 'brand',
    features: [
      {
        icon: Target,
        title: 'My Tasks Dashboard',
        description: 'See all your assigned tasks in one place with priorities, deadlines, and status.',
        tone: 'brand',
      },
      {
        icon: Clock,
        title: 'Time Tracking',
        description: 'Log hours easily with detailed descriptions. Track estimates vs actuals.',
        tone: 'success',
      },
      {
        icon: MessageSquare,
        title: 'Standup Notes',
        description: 'Quick daily updates. Share progress and blockers with your team.',
        tone: 'insight',
      },
      {
        icon: Award,
        title: 'Performance Ratings',
        description: 'Get feedback on your work. See your performance metrics and ratings.',
        tone: 'warning',
      },
      {
        icon: FileText,
        title: 'Task Details',
        description: 'Rich task descriptions, attachments, external links, and verification.',
        tone: 'support',
      },
      {
        icon: GitBranch,
        title: 'Sprint View',
        description: 'See your sprint workload, velocity, and burndown charts.',
        tone: 'accent',
      },
    ],
    benefits: [
      {
        title: 'Clear Priorities',
        description: 'Always know what to work on next with smart task prioritization',
      },
      {
        title: 'Transparent Communication',
        description: 'Keep everyone updated with standup notes and progress tracking',
      },
      {
        title: 'Fair Recognition',
        description: 'Your work is tracked and rated for performance reviews',
      },
      {
        title: 'Career Growth',
        description: 'Performance metrics help you identify areas for improvement',
      },
    ],
    workflow: [
      {
        title: 'Check Your Dashboard',
        description: 'Start your day by reviewing assigned tasks and priorities',
      },
      {
        title: 'Update Task Status',
        description: 'Move tasks through workflow as you make progress',
      },
      {
        title: 'Log Your Time',
        description: 'Track hours worked with detailed descriptions',
      },
      {
        title: 'Add Standup Notes',
        description: 'Share daily progress and any blockers with the team',
      },
      {
        title: 'Complete & Get Rated',
        description: 'Mark tasks complete and receive quality ratings',
      },
    ],
    testimonial:
      'ProjectFlow keeps me organized and focused. I love seeing my progress and performance metrics.',
    testimonialAuthor: 'Masud Developer, Senior Software Engineer',
  },
  manager: {
    title: 'For Project Managers',
    description:
      'Plan, execute, and deliver projects successfully. Manage sprints, allocate resources, and keep your team on track.',
    ctaText: 'Start Managing',
    icon: Briefcase,
    tone: 'accent',
    features: [
      {
        icon: Calendar,
        title: 'Sprint Management',
        description: 'Plan sprints, set goals, track velocity, and monitor burndown charts.',
        tone: 'accent',
      },
      {
        icon: Users,
        title: 'Team Coordination',
        description: 'Assign tasks, balance workload, and manage team capacity.',
        tone: 'brand',
      },
      {
        icon: BarChart3,
        title: 'Progress Tracking',
        description: 'Real-time visibility into project progress and team performance.',
        tone: 'success',
      },
      {
        icon: Target,
        title: 'Planning Board',
        description: 'Visual planning with drag-and-drop task organization.',
        tone: 'support',
      },
      {
        icon: FileText,
        title: 'Retrospectives',
        description: 'Conduct team retrospectives and track action items.',
        tone: 'warning',
      },
      {
        icon: TrendingUp,
        title: 'Analytics Dashboard',
        description: 'Insights into team velocity, task completion, and bottlenecks.',
        tone: 'insight',
      },
    ],
    benefits: [
      {
        title: 'Better Planning',
        description: 'Data-driven sprint planning with velocity tracking',
      },
      {
        title: 'Team Visibility',
        description: 'Know exactly what everyone is working on in real-time',
      },
      {
        title: 'Early Risk Detection',
        description: 'Identify blockers and issues before they become problems',
      },
      {
        title: 'Improved Delivery',
        description: 'Consistently hit deadlines with better resource management',
      },
    ],
    workflow: [
      {
        title: 'Sprint Planning',
        description: 'Review backlog, estimate tasks, and plan sprint goals',
      },
      {
        title: 'Task Assignment',
        description: 'Assign tasks to team members based on capacity and skills',
      },
      {
        title: 'Daily Monitoring',
        description: 'Review standup notes and track progress daily',
      },
      {
        title: 'Address Blockers',
        description: 'Identify and resolve team blockers quickly',
      },
      {
        title: 'Sprint Review',
        description: 'Conduct retrospectives and capture learnings',
      },
    ],
    testimonial:
      "The best project management tool I've used. Keeps my team aligned and productive.",
    testimonialAuthor: 'Sarah Manager, Engineering Manager',
  },
  executive: {
    title: 'For Executives & Stakeholders',
    description:
      'High-level visibility into projects, costs, and team performance. Make data-driven decisions with confidence.',
    ctaText: 'View Dashboard',
    icon: BarChart3,
    tone: 'success',
    features: [
      {
        icon: TrendingUp,
        title: 'Executive Dashboard',
        description: 'High-level overview of all projects, progress, and key metrics.',
        tone: 'success',
      },
      {
        icon: DollarSign,
        title: 'Cost Tracking',
        description: 'Monitor project costs, budget vs actual, and ROI metrics.',
        tone: 'brand',
      },
      {
        icon: Calendar,
        title: 'Timeline View',
        description: 'See project timelines, milestones, and delivery dates at a glance.',
        tone: 'accent',
      },
      {
        icon: Users,
        title: 'Team Performance',
        description: 'Developer ratings, task completion rates, and productivity metrics.',
        tone: 'support',
      },
      {
        icon: MessageSquare,
        title: 'Standup Summaries',
        description: 'Review team updates and progress across any date range.',
        tone: 'warning',
      },
      {
        icon: FileText,
        title: 'Custom Reports',
        description: 'Generate detailed reports for stakeholders and board meetings.',
        tone: 'insight',
      },
    ],
    benefits: [
      {
        title: 'Strategic Visibility',
        description: 'Real-time visibility into project health and progress',
      },
      {
        title: 'Financial Control',
        description: 'Track costs and ensure projects stay within budget',
      },
      {
        title: 'Data-Driven Decisions',
        description: 'Make informed decisions with comprehensive analytics',
      },
      {
        title: 'Risk Management',
        description: 'Identify risks early and take corrective action',
      },
    ],
    workflow: [
      {
        title: 'Review Dashboard',
        description: 'Check executive dashboard for project status and KPIs',
      },
      {
        title: 'Monitor Progress',
        description: 'Review project timelines and milestone completion',
      },
      {
        title: 'Check Financials',
        description: 'Monitor costs, budget utilization, and ROI',
      },
      {
        title: 'Review Team Performance',
        description: 'Assess developer ratings and productivity metrics',
      },
      {
        title: 'Generate Reports',
        description: 'Create reports for stakeholders and board meetings',
      },
    ],
    testimonial:
      'Finally, a tool that gives me the visibility I need without overwhelming me with details.',
    testimonialAuthor: 'Mike Investor, Board Member',
  },
  investor: {
    title: 'For Investors',
    description:
      'Track your investment with real-time project metrics, financial performance, and team productivity.',
    ctaText: 'View Portfolio',
    icon: DollarSign,
    tone: 'success',
    features: [
      {
        icon: DollarSign,
        title: 'ROI Tracking',
        description: 'Monitor return on investment with detailed financial metrics.',
        tone: 'success',
      },
      {
        icon: TrendingUp,
        title: 'Growth Metrics',
        description: 'Track project velocity, team growth, and delivery trends.',
        tone: 'brand',
      },
      {
        icon: BarChart3,
        title: 'Portfolio View',
        description: 'Overview of all projects across your investment portfolio.',
        tone: 'accent',
      },
    ],
    benefits: [
      {
        title: 'Financial Transparency',
        description: 'Complete visibility into how your investment is being used',
      },
      {
        title: 'Progress Tracking',
        description: 'Monitor project milestones and delivery timelines',
      },
      {
        title: 'Risk Assessment',
        description: 'Identify potential risks and issues early',
      },
    ],
    workflow: [
      {
        title: 'Review Portfolio',
        description: 'Check overall portfolio health and key metrics',
      },
      {
        title: 'Monitor Financials',
        description: 'Track costs, budget burn rate, and ROI',
      },
      {
        title: 'Assess Progress',
        description: 'Review project timelines and milestone completion',
      },
    ],
    testimonial:
      'Transparent metrics and clear visibility into project progress. Exactly what investors need.',
    testimonialAuthor: 'Mike Investor, Venture Capital',
  },
  hr: {
    title: 'For HR & People Operations',
    description:
      'Track employee performance, workload distribution, and team dynamics with comprehensive people analytics.',
    ctaText: 'Explore Features',
    icon: Users,
    tone: 'support',
    features: [
      {
        icon: Award,
        title: 'Performance Analytics',
        description: 'Track developer ratings, task completion, and quality metrics.',
        tone: 'support',
      },
      {
        icon: Users,
        title: 'Workload Balance',
        description: 'Monitor employee workload and identify burnout risks.',
        tone: 'brand',
      },
      {
        icon: TrendingUp,
        title: 'Growth Tracking',
        description: 'Track employee growth, skill development, and career progression.',
        tone: 'success',
      },
    ],
    benefits: [
      {
        title: 'Fair Performance Reviews',
        description: 'Objective data for performance evaluations',
      },
      {
        title: 'Employee Well-being',
        description: 'Monitor workload to prevent burnout',
      },
      {
        title: 'Career Development',
        description: 'Track skills and identify training needs',
      },
    ],
    workflow: [
      {
        title: 'Review Performance',
        description: 'Check employee ratings and productivity metrics',
      },
      {
        title: 'Monitor Workload',
        description: 'Ensure balanced task distribution across team',
      },
      {
        title: 'Conduct Reviews',
        description: 'Use data for fair and objective performance reviews',
      },
    ],
    testimonial:
      'Makes performance reviews objective and fair. Great insights into team dynamics.',
    testimonialAuthor: 'Emily HR, People Operations',
  },
  partner: {
    title: 'For External Partners',
    description:
      'Collaborate seamlessly with limited access to relevant project information and updates.',
    ctaText: 'Partner Login',
    icon: Briefcase,
    tone: 'insight',
    features: [
      {
        icon: FileText,
        title: 'Project Access',
        description: "View project details, timelines, and deliverables you're involved with.",
        tone: 'insight',
      },
      {
        icon: MessageSquare,
        title: 'Communication Hub',
        description: 'Stay updated with project progress and team communications.',
        tone: 'brand',
      },
      {
        icon: Calendar,
        title: 'Milestone Tracking',
        description: 'Monitor key milestones and delivery dates.',
        tone: 'accent',
      },
    ],
    benefits: [
      {
        title: 'Seamless Collaboration',
        description: 'Work with internal teams without full system access',
      },
      {
        title: 'Stay Informed',
        description: 'Get updates on relevant projects and milestones',
      },
      {
        title: 'Secure Access',
        description: 'Role-based permissions ensure data security',
      },
    ],
    workflow: [
      {
        title: 'View Projects',
        description: "Check status of projects you're partnering on",
      },
      {
        title: 'Review Deliverables',
        description: 'Track milestones and delivery dates',
      },
      {
        title: 'Communicate',
        description: 'Stay in touch with project updates',
      },
    ],
    testimonial:
      'Easy collaboration without complexity. Perfect for external partners.',
    testimonialAuthor: 'David Partner, External Consultant',
  },
  demo: {
    title: 'ProjectFlow Demo',
    description:
      'Experience the power of complete project lifecycle management. See how ProjectFlow helps teams deliver projects on time and within budget.',
    ctaText: 'Explore Demo',
    icon: Target,
    tone: 'brand',
    features: [
      {
        icon: Target,
        title: 'Complete Lifecycle',
        description: 'Manage everything from proposals to delivery in one platform.',
        tone: 'brand',
      },
      {
        icon: Users,
        title: 'Role-Based Access',
        description: 'Each team member sees exactly what they need, nothing more.',
        tone: 'accent',
      },
      {
        icon: BarChart3,
        title: 'Powerful Analytics',
        description: 'Data-driven insights for better decision making.',
        tone: 'success',
      },
    ],
    benefits: [
      {
        title: 'Increased Productivity',
        description: 'Teams report 40% faster project delivery',
      },
      {
        title: 'Better Visibility',
        description: 'Real-time insights across all projects',
      },
      {
        title: 'Cost Savings',
        description: 'Reduce project overruns by 30%',
      },
    ],
    workflow: [
      {
        title: 'Create Project',
        description: 'Set up projects with proposals and cost estimates',
      },
      {
        title: 'Plan Sprints',
        description: 'Organize work into manageable sprints',
      },
      {
        title: 'Track Progress',
        description: 'Monitor tasks, time, and team performance',
      },
      {
        title: 'Deliver Results',
        description: 'Complete projects on time and within budget',
      },
    ],
    testimonial:
      'ProjectFlow transformed how we manage projects. Highly recommended!',
    testimonialAuthor: 'Sample Organization',
  },
};

function getStakeholderData(type: StakeholderType): StakeholderData {
  return STAKEHOLDER_DATA[type] || STAKEHOLDER_DATA.demo;
}

export default function StakeholderLanding() {
  const navigate = useNavigate();
  const { type } = useParams<{ type?: StakeholderType }>();
  const stakeholderType =
    type && Object.prototype.hasOwnProperty.call(STAKEHOLDER_DATA, type)
      ? (type as StakeholderType)
      : 'demo';
  const stakeholderData = getStakeholderData(stakeholderType);
  const heroTone = STAKEHOLDER_TONE_STYLES[stakeholderData.tone];
  const HeroIcon = stakeholderData.icon;

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-background to-muted/30">
      <nav className="sticky top-0 z-50 border-border bg-background/95 shadow-sm backdrop-blur">
        <div className={`${PAGE_SECTION_CLASS} py-4`}>
          <div className="flex items-center justify-between">
            <h1 className="bg-gradient-to-r from-chart-1 via-chart-3 to-chart-4 bg-clip-text text-2xl font-bold text-transparent">
              ProjectFlow
            </h1>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => navigate('/project')}>
                Back
              </Button>
              <Button onClick={() => navigate('/login')}>Sign In</Button>
            </div>
          </div>
        </div>
      </nav>

      <section className={`${PAGE_SECTION_CLASS} py-20`}>
        <div className="mx-auto max-w-4xl text-center">
          <div className={`mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full ${heroTone.panel}`}>
            <HeroIcon className={`h-10 w-10 ${heroTone.icon}`} />
          </div>
          <h1 className="mb-6 text-5xl font-bold tracking-tight text-foreground">{stakeholderData.title}</h1>
          <p className="mb-8 text-xl leading-relaxed text-muted-foreground">{stakeholderData.description}</p>
          <div className="flex flex-col justify-center gap-4 sm:flex-row">
            <Button size="lg" onClick={() => navigate('/app')} className="px-8 py-6 text-lg">
              {stakeholderData.ctaText} <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate('/dashboard')}
              className="px-8 py-6 text-lg"
            >
              View Demo Dashboard
            </Button>
          </div>
        </div>
      </section>

      <section className={`${PAGE_SECTION_CLASS} py-20`}>
        <h2 className="mb-12 text-center text-4xl font-bold text-foreground">Key Features for You</h2>
        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
          {stakeholderData.features.map((feature) => {
            const FeatureIcon = feature.icon;
            const featureTone = STAKEHOLDER_TONE_STYLES[feature.tone];

            return (
              <div key={feature.title} className={`${SURFACE_CARD_CLASS} hover:shadow-lg`}>
                <div className={`mb-4 flex h-12 w-12 items-center justify-center rounded-lg ${featureTone.panel}`}>
                  <FeatureIcon className={`h-6 w-6 ${featureTone.icon}`} />
                </div>
                <h3 className="mb-2 text-xl font-semibold text-foreground">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className={`${HIGHLIGHT_SECTION_CLASS} py-20`}>
        <div className={PAGE_SECTION_CLASS}>
          <h2 className="mb-12 text-center text-4xl font-bold text-foreground">Why Teams Love ProjectFlow</h2>
          <div className="mx-auto grid max-w-4xl grid-cols-1 gap-8 md:grid-cols-2">
            {stakeholderData.benefits.map((benefit) => (
              <div key={benefit.title} className={`${SURFACE_CARD_CLASS} flex items-start gap-4`}>
                <CheckCircle className="mt-1 h-6 w-6 flex-shrink-0 text-chart-2" />
                <div>
                  <h4 className="mb-1 font-semibold text-foreground">{benefit.title}</h4>
                  <p className="text-sm text-muted-foreground">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className={`${PAGE_SECTION_CLASS} py-20`}>
        <h2 className="mb-12 text-center text-4xl font-bold text-foreground">Your Typical Workflow</h2>
        <div className="mx-auto max-w-4xl">
          <div className="space-y-6">
            {stakeholderData.workflow.map((step, index) => (
              <div key={step.title} className="flex items-start gap-4">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border border-primary/20 bg-primary/10 text-sm font-bold text-primary">
                  {index + 1}
                </div>
                <div className={`${SURFACE_CARD_CLASS} flex-1`}>
                  <h4 className="mb-2 font-semibold text-foreground">{step.title}</h4>
                  <p className="text-muted-foreground">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className={`${HIGHLIGHT_SECTION_CLASS} py-20`}>
        <div className={PAGE_SECTION_CLASS}>
          <div className="mx-auto max-w-3xl rounded-3xl border border-border bg-card/85 px-8 py-12 text-center shadow-sm backdrop-blur">
            <div className="mb-6 text-6xl text-chart-1">"</div>
            <p className="mb-6 text-2xl leading-relaxed text-foreground">{stakeholderData.testimonial}</p>
            <p className="text-xl text-muted-foreground">{stakeholderData.testimonialAuthor}</p>
          </div>
        </div>
      </section>

      <section className={`${PAGE_SECTION_CLASS} py-20 text-center`}>
        <div className="mx-auto max-w-3xl">
          <h2 className="mb-6 text-4xl font-bold text-foreground">Ready to Get Started?</h2>
          <p className="mb-8 text-xl text-muted-foreground">
            Join thousands of teams already using ProjectFlow for their project management needs
          </p>
          <Button size="lg" onClick={() => navigate('/app')} className="px-8 py-6 text-lg">
            Start Free Trial <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      <footer className="border-t border-border bg-card py-8">
        <div className={`${PAGE_SECTION_CLASS} text-center`}>
          <p className="text-muted-foreground">© 2026 ProjectFlow by Intellesphere Ltd. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
